import statuses from 'statuses';
/**
 * Intercepts failed HTTP requests and validates if they failed because the server returned an
 * unauthorized status. If they did, the service will trigger an event for the app to react.
 * @ngesdoc service
 * @ngname ssoAuthInterceptor
 * @ngmodule common
 */
class SSOAuthInterceptor {
  /**
   * @param {ngLocation}        $location         To make sure the user is not on the login
   *                                              screen, where an unauthorized response is just a
   *                                              failed attempt.
   * @param {ngQ}               $q                To return a rejected promise.
   * @param {AppEvents}         appEvents         To trigger the event informing of the
   *                                              unauthorized request.
   * @param {AuthRoutesManager} authRoutesManager To compare with the current URL and validate
   *                                              that the user is not on the login screen.
   * @param {AuthEvents}        AUTH_EVENTS       To get the name of the event the service may
   *                                              trigger.
   */
  constructor($location, $q, appEvents, authRoutesManager, AUTH_EVENTS) {
    'ngInject';

    /**
     * A local reference for the `$location` service.
     * @type {ngLocation}
     */
    this.$location = $location;
    /**
     * A local reference for the `$q` service.
     * @type {ngQ}
     */
    this.$q = $q;
    /**
     * A local reference for the `appEvents` service.
     * @type {AppEvents}
     */
    this.appEvents = appEvents;
    /**
     * A local reference for the `authRoutesManager` service.
     * @type {AuthRoutesManager}
     */
    this.authRoutesManager = authRoutesManager;
    /**
     * A local reference for the `AUTH_EVENTS` constant.
     * @type {AuthEvents}
     */
    this.AUTH_EVENTS = AUTH_EVENTS;
    /**
     * Bind the method as it will be sent as an `$http` interceptor.
     * @ignore
     */
    this.responseError = this.responseError.bind(this);
  }
  /**
   * Intercepts a failed HTTP request response and validates if it failed because the server
   * returned an unauthorized status; if that's the case and the user is not on the login screen,
   * the method will trigger an event so the app can handle the situation.
   * @param {Object} response The response of the failed HTTP request.
   * @return {Promise<undefined,Error>}
   */
  responseError(response) {
    if (
      // If the user is not on the login screen.
      !this.authRoutesManager.isLogin(this.$location.path()) &&
      // If the HTTP status is 401.
      response.status === statuses('unauthorized')
    ) {
      // Emit the event.
      this.appEvents.emit(this.AUTH_EVENTS.unauthorized);
    }

    // Return the rejected promise to keep the flow.
    return this.$q.reject(response);
  }
}

export default SSOAuthInterceptor;
